import React, { useEffect, useState } from "react";
import { Save, Plus, Trash2, UploadCloud, X } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { PageHeader, adminBtnPrimary, adminField, adminLabel } from "@/components/admin/ui";
import { ImageQuad } from "@/components/site/ImageQuad";

const TABS = [
  ["company", "Company"],
  ["home", "Home & Featured"],
  ["services", "Services"],
  ["financial_planning", "Financial Planning"],
  ["about", "About"],
  ["seo", "SEO"],
  ["legal", "Legal"],
];

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [tab, setTab] = useState("company");
  const [articles, setArticles] = useState([]);
  const [videos, setVideos] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {});
    api.get("/admin/articles").then((r) => setArticles(r.data.filter((a) => a.status === "published"))).catch(() => {});
    api.get("/admin/videos").then((r) => setVideos(r.data.filter((v) => v.status === "published"))).catch(() => {});
  }, []);

  const up = (sec, key, val) => setS((p) => ({ ...p, [sec]: { ...p[sec], [key]: val } }));
  const upItem = (sec, idx, key, val) => setS((p) => ({ ...p, [sec]: { ...p[sec], items: p[sec].items.map((it, i) => (i === idx ? { ...it, [key]: val } : it)) } }));
  const addItem = (sec) => setS((p) => ({ ...p, [sec]: { ...p[sec], items: [...(p[sec].items || []), { title: "", body: "" }] } }));
  const delItem = (sec, idx) => setS((p) => ({ ...p, [sec]: { ...p[sec], items: p[sec].items.filter((_, i) => i !== idx) } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", s);
      setS(data);
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      up("about", "photo", data.url);
      toast.success("Photo uploaded");
    } catch { toast.error("Upload failed"); }
  };

  const emptyQuad = [{ image: "", alt: "" }, { image: "", alt: "" }, { image: "", alt: "" }, { image: "", alt: "" }];
  const setQuad = (i, k, v) =>
    setS((p) => {
      const quad = [...((p.home && p.home.quad) || emptyQuad)];
      quad[i] = { ...quad[i], [k]: v };
      return { ...p, home: { ...p.home, quad } };
    });
  const uploadQuad = async (i, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setQuad(i, "image", data.url);
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
  };

  const uploadHero = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      up("home", "hero_image", data.url);
      toast.success("Portrait uploaded");
    } catch { toast.error("Upload failed"); }
  };

  if (!s) return <p className="py-20 text-center font-sans text-steel-400">Loading…</p>;

  return (
    <>
      <PageHeader title="Site Settings" subtitle="Edit company information, homepage features, services and legal pages.">
        <button onClick={save} disabled={saving} className={adminBtnPrimary} data-testid="settings-save"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save changes"}</button>
      </PageHeader>

      <div className="mb-8 flex flex-wrap gap-2 border-b border-ink/10">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} className={`-mb-px border-b-2 px-4 py-2.5 font-sans text-sm ${tab === k ? "border-gold-500 text-gold-600" : "border-transparent text-steel-500 hover:text-ink"}`} data-testid={`settings-tab-${k}`}>{label}</button>
        ))}
      </div>

      <div className="max-w-3xl space-y-5">
        {tab === "company" && (
          <>
            {[["name", "Company name"], ["principal", "Principal"], ["tagline_gold", "Tagline (gold word)"], ["tagline_steel", "Tagline (steel word)"], ["phone", "Phone (display)"], ["phone_link", "Phone (tel: link, e.g. +16318040212)"], ["website", "Website"], ["address", "Address"]].map(([k, l]) => (
              <div key={k}><label className={adminLabel}>{l}</label><input value={s.company[k] || ""} onChange={(e) => up("company", k, e.target.value)} className={adminField} data-testid={`company-${k}`} /></div>
            ))}
          </>
        )}

        {tab === "home" && (
          <>
            <div><label className={adminLabel}>Intro heading</label><textarea rows={2} value={s.home.intro_heading || ""} onChange={(e) => up("home", "intro_heading", e.target.value)} className={adminField} /></div>
            <div><label className={adminLabel}>Intro body</label><textarea rows={3} value={s.home.intro_body || ""} onChange={(e) => up("home", "intro_body", e.target.value)} className={adminField} /></div>
            <div>
              <label className={adminLabel}>Featured article</label>
              <select value={s.home.featured_article_id || ""} onChange={(e) => up("home", "featured_article_id", e.target.value || null)} className={adminField} data-testid="featured-article-select">
                <option value="">Most recent (automatic)</option>
                {articles.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
              </select>
            </div>
            <div>
              <label className={adminLabel}>Featured video</label>
              <select value={s.home.featured_video_id || ""} onChange={(e) => up("home", "featured_video_id", e.target.value || null)} className={adminField} data-testid="featured-video-select">
                <option value="">Most recent (automatic)</option>
                {videos.map((v) => <option key={v.id} value={v.id}>{v.title}</option>)}
              </select>
            </div>
            <div><label className={adminLabel}>Consultation heading</label><input value={s.home.consult_heading || ""} onChange={(e) => up("home", "consult_heading", e.target.value)} className={adminField} /></div>
            <div><label className={adminLabel}>Consultation body</label><textarea rows={2} value={s.home.consult_body || ""} onChange={(e) => up("home", "consult_body", e.target.value)} className={adminField} /></div>

            <div>
              <label className={adminLabel}>Homepage hero portrait (single image)</label>
              <p className="mt-1 font-sans text-xs text-steel-500">This is the single portrait shown in the homepage introduction. Uses the 515:646 proportion. If left empty, the principal portrait (quad panel 1) is used.</p>
              {s.home.hero_image ? (
                <div className="relative mt-2 max-w-[220px]">
                  <img src={mediaUrl(s.home.hero_image)} alt="" className="aspect-[515/646] w-full object-cover" data-testid="hero-image-preview" />
                  <button onClick={() => up("home", "hero_image", "")} className="absolute right-1 top-1 rounded-full bg-ink/70 p-1 text-cream-100" aria-label="Remove hero portrait"><X className="h-3 w-3" /></button>
                </div>
              ) : (
                <label className="mt-2 flex max-w-[220px] cursor-pointer flex-col items-center justify-center border border-dashed border-ink/20 py-8 text-steel-400 hover:border-gold-500">
                  <UploadCloud className="h-5 w-5" /><span className="mt-1 font-sans text-xs">Upload portrait</span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadHero(e.target.files[0])} data-testid="hero-image-upload" />
                </label>
              )}
              <input value={s.home.hero_alt || ""} onChange={(e) => up("home", "hero_alt", e.target.value)} placeholder="Portrait description (alt)" className={adminField} data-testid="hero-alt-input" />
            </div>

            <div>
              <label className={adminLabel}>2×2 Image Quad — “About the Principal” section</label>
              <p className="mt-1 font-sans text-xs text-steel-500">This 2×2 quad replaces the single portrait in the <strong>“About the Principal”</strong> section. It goes live only once <strong>all four panels</strong> have images — until then the existing single portrait stays visible and no empty placeholders are shown to visitors. Panel 1 (upper-left) is the principal portrait; each panel keeps the 515:646 proportion and is independently replaceable.</p>
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[0, 1, 2, 3].map((i) => {
                  const q = (s.home.quad && s.home.quad[i]) || { image: "", alt: "" };
                  return (
                    <div key={i} className="border border-ink/10 bg-white p-3">
                      <span className="font-sans text-xs text-steel-400">Panel {i + 1}{i === 0 ? " · portrait" : ""}</span>
                      {q.image ? (
                        <div className="relative mt-2">
                          <img src={mediaUrl(q.image)} alt="" className="aspect-[515/646] w-full object-cover" />
                          <button onClick={() => setQuad(i, "image", "")} className="absolute right-1 top-1 rounded-full bg-ink/70 p-1 text-cream-100" aria-label={`Remove panel ${i + 1} image`}><X className="h-3 w-3" /></button>
                        </div>
                      ) : (
                        <label className="mt-2 flex aspect-[515/646] cursor-pointer flex-col items-center justify-center border border-dashed border-ink/20 text-steel-400 hover:border-gold-500">
                          <UploadCloud className="h-5 w-5" />
                          <span className="mt-1 font-sans text-[10px]">Upload</span>
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadQuad(i, e.target.files[0])} data-testid={`quad-upload-${i}`} />
                        </label>
                      )}
                      <input value={q.alt} onChange={(e) => setQuad(i, "alt", e.target.value)} placeholder="Image description (alt)" className={adminField} data-testid={`quad-alt-${i}`} />
                    </div>
                  );
                })}
              </div>
              <div className="mt-4">
                <span className="font-sans text-xs text-steel-400">Assembled quad preview (editor only)</span>
                <div className="mt-2 max-w-[300px]" data-testid="quad-preview">
                  <ImageQuad panels={s.home.quad || []} />
                </div>
              </div>
            </div>
          </>
        )}

        {(tab === "services" || tab === "financial_planning") && (
          <>
            {"heading" in s[tab] && <div><label className={adminLabel}>Heading</label><input value={s[tab].heading || ""} onChange={(e) => up(tab, "heading", e.target.value)} className={adminField} /></div>}
            <div><label className={adminLabel}>Intro</label><textarea rows={3} value={s[tab].intro || ""} onChange={(e) => up(tab, "intro", e.target.value)} className={adminField} /></div>
            {"body" in s[tab] && <div><label className={adminLabel}>Body</label><textarea rows={3} value={s[tab].body || ""} onChange={(e) => up(tab, "body", e.target.value)} className={adminField} /></div>}
            <div className="flex items-center justify-between"><label className={adminLabel}>Items</label><button onClick={() => addItem(tab)} className="flex items-center gap-1 font-sans text-xs text-gold-600"><Plus className="h-3 w-3" /> Add item</button></div>
            {(s[tab].items || []).map((it, i) => (
              <div key={i} className="border border-ink/10 bg-cream-100 p-4">
                <div className="flex items-center justify-between"><span className="font-sans text-xs text-steel-400">Item {i + 1}</span><button onClick={() => delItem(tab, i)} className="text-steel-400 hover:text-destructive"><Trash2 className="h-4 w-4" /></button></div>
                <input value={it.title} onChange={(e) => upItem(tab, i, "title", e.target.value)} placeholder="Title" className={adminField} />
                <textarea rows={2} value={it.body} onChange={(e) => upItem(tab, i, "body", e.target.value)} placeholder="Description" className={adminField} />
              </div>
            ))}
            {"disclaimer" in s[tab] && <div><label className={adminLabel}>Disclaimer</label><textarea rows={3} value={s[tab].disclaimer || ""} onChange={(e) => up(tab, "disclaimer", e.target.value)} className={adminField} /></div>}
          </>
        )}

        {tab === "about" && (
          <>
            <div><label className={adminLabel}>Name & credentials</label><input value={s.about.name || ""} onChange={(e) => up("about", "name", e.target.value)} className={adminField} /></div>
            <div>
              <label className={adminLabel}>Professional photo</label>
              {s.about.photo ? (
                <div className="relative mt-2 max-w-xs"><img src={mediaUrl(s.about.photo)} alt="" className="aspect-[4/5] w-full object-cover" /><button onClick={() => up("about", "photo", "")} className="absolute right-2 top-2 rounded-full bg-ink/70 p-1 text-cream-100"><X className="h-4 w-4" /></button></div>
              ) : (
                <label className="mt-2 flex max-w-xs cursor-pointer flex-col items-center justify-center border border-dashed border-ink/20 py-8 text-steel-400 hover:border-gold-500"><UploadCloud className="h-6 w-6" /><span className="mt-2 font-sans text-xs">Upload photo</span><input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files[0])} /></label>
              )}
            </div>
            <div><label className={adminLabel}>Biography</label><textarea rows={6} value={s.about.bio || ""} onChange={(e) => up("about", "bio", e.target.value)} className={adminField} /></div>
            <div><label className={adminLabel}>Note</label><textarea rows={2} value={s.about.note || ""} onChange={(e) => up("about", "note", e.target.value)} className={adminField} /></div>
          </>
        )}

        {tab === "seo" && (
          <>
            <div><label className={adminLabel}>Default page title</label><input value={s.seo.default_title || ""} onChange={(e) => up("seo", "default_title", e.target.value)} className={adminField} /></div>
            <div><label className={adminLabel}>Default meta description</label><textarea rows={3} value={s.seo.default_description || ""} onChange={(e) => up("seo", "default_description", e.target.value)} className={adminField} /></div>
          </>
        )}

        {tab === "legal" && (
          <>
            <div><label className={adminLabel}>Privacy Policy</label><textarea rows={12} value={s.legal.privacy || ""} onChange={(e) => up("legal", "privacy", e.target.value)} className={`${adminField} font-mono text-xs`} data-testid="legal-privacy" /></div>
            <div><label className={adminLabel}>Terms of Use</label><textarea rows={10} value={s.legal.terms || ""} onChange={(e) => up("legal", "terms", e.target.value)} className={`${adminField} font-mono text-xs`} data-testid="legal-terms" /></div>
          </>
        )}
      </div>
    </>
  );
}
